/**
 * Internal Platform Sync-Config CATALOG API Contract (SCH-1250)
 *
 * POST /internal/platform-data-sync/platform-config — create the platform-wide
 *                                                     catalog row for one sync type
 * GET  /internal/platform-data-sync/platform-config?sync_type= — read it back
 *
 * API-key-gated (`INTERNAL_API_KEY`). This is the internal-lane counterpart of
 * the Clerk-gated super-admin writer
 * (`POST /system/v1/platform-sync-configs`), so an operator or an agent can
 * mint the row without a browser session.
 *
 * WHAT THE CATALOG ROW IS. One `SystemConfiguration` row with
 * `category = 'PLATFORM_SYNC_DEFAULTS'`, `key = <sync type>` and
 * `school_id IS NULL`, whose `value` carries the fleet-wide defaults
 * (`frequencyMinutes`, `priority`, `window`, `isEnabled`, `description`,
 * `syncGroup`). It is NOT a per-school switch: nothing runs because this row
 * exists. It is the fleet OVERRIDE that `ensureConfig` applies when it seeds a
 * school's `PlatformDataSyncConfig` the first time that school runs the sync
 * type. Since the Sync Catalog (SCH-2681) the row is optional: without it the
 * seed comes from the step descriptor's own schedule. Before that, a missing row
 * made the seed throw ("No system default configuration found for sync type").
 *
 * DISTINCT from the per-school lanes: `POST /internal/platform-data-sync/sync-config`
 * toggles a school's `PlatformDataSyncConfig.is_enabled`, and
 * `PATCH /sync-config/settings` writes that row's tunables. Both need the
 * per-school row to already exist; this endpoint is what makes it possible.
 *
 * CREATE IS NOT AN UPSERT. A catalog row that already exists is a 409 — editing
 * the fleet defaults is the separate platform-config update path, which also
 * pushes the change out to every school. This endpoint only ever mints.
 *
 * OPTIONAL SEEDING. `school_ids` additionally clones the fresh catalog row into
 * each listed school via the same `findOrCreateSyncConfig` primitive (backed by
 * `ensureConfig`) the sync pipeline itself uses. That is per-school BEST-EFFORT: an unresolvable school,
 * or one whose seed throws (e.g. a sync type with no registered step), is a
 * per-school result, never a 4xx/5xx — the catalog row was still created.
 */

import { z } from 'zod'
import { SyncPrioritySchema, SyncWindowSchema } from '../../schemas/entities/sync'
// The pre-archive bulk disable is literally the `POST /sync-config` primitive,
// so its per-school result shape is reused rather than restated (SCH-1965).
import { SyncConfigToggleResultSchema } from './sync-config-toggle'

/**
 * Sync group values, kept in lock-step with the backend runtime enum
 * (`apps/backend/src/types/sync-enums.ts` `SYNC_GROUP_VALUES`). The group is a
 * reporting/bucketing label on the config — it does not affect scheduling.
 */
export const SYNC_GROUP_VALUES = [
  'ACADEMIC',
  'COMMUNICATION',
  'USER_MANAGEMENT',
  'REFERENCE_DATA',
  'CONTENT',
  'SECTIONS',
  'EVENTS',
  'SINGLE_USER_DATA',
  'MEDIA',
  'ENROLLMENT'
] as const

export const SyncGroupSchema = z.enum(SYNC_GROUP_VALUES)
export type SyncGroup = z.infer<typeof SyncGroupSchema>

/**
 * POST request body. snake_case, matching the sibling internal routes.
 *
 * `sync_type` is a plain string here rather than an enum: the authoritative
 * registry is the backend's `isValidSyncType` (which knows about deregistered
 * and shelved types), and duplicating ~70 members into the shared contract is
 * how the two lists drift. An unregistered value is a 400 from the use-case,
 * naming the type.
 */
export const CreatePlatformSyncConfigCatalogRequestSchema = z.object({
  sync_type: z.string().min(1).describe('Sync type the catalog row is for (e.g. ACADEMIC_ENROLLMENT_DELTA)'),
  frequency_minutes: z.number().int().min(1).describe('Default staleness interval in minutes'),
  priority: SyncPrioritySchema.describe('Default queue priority'),
  window: SyncWindowSchema.describe('Default run window (most values name a SKIP window — see SYNC_WINDOW_LABELS)'),
  sync_group: SyncGroupSchema.describe('Reporting group the sync type belongs to'),
  is_enabled: z.boolean().default(false).describe('Default is_enabled cloned into each school (defaults to false)'),
  description: z.string().min(1).optional().describe('Human description; defaults to one derived from the sync type'),
  school_ids: z
    .array(z.string().min(1))
    .optional()
    .describe('Optional schools to seed a per-school config for (each a public ID (CUID) or slug)'),
  reason: z.string().max(2000).optional().describe('Free-text audit reason, recorded in the logs'),
  changed_by: z
    .string()
    .max(200)
    .optional()
    .describe("Optional caller identity for the audit trail (defaults to 'internal-api')")
})

/** The PARSED body (`is_enabled` defaulted), which is what the use-case receives. */
export type CreatePlatformSyncConfigCatalogRequest = z.infer<typeof CreatePlatformSyncConfigCatalogRequestSchema>

/**
 * The catalog row as the wire carries it. `id` is the `SystemConfiguration`
 * public ID; timestamps are ISO strings.
 */
export const PlatformSyncConfigCatalogRowSchema = z.object({
  id: z.string().describe('Public ID of the SystemConfiguration row'),
  syncType: z.string().describe('The sync type this row is the platform default for'),
  frequencyMinutes: z.number().int(),
  priority: SyncPrioritySchema,
  window: SyncWindowSchema,
  syncGroup: SyncGroupSchema,
  isEnabled: z.boolean(),
  description: z.string(),
  createdAt: z.string().describe('ISO timestamp the row was created'),
  updatedAt: z.string().nullable().describe('ISO timestamp of the last write, or null')
})

export type PlatformSyncConfigCatalogRow = z.infer<typeof PlatformSyncConfigCatalogRowSchema>

/**
 * Per-school outcome of the optional `school_ids` seeding pass.
 *  - `resolved: false` → the school_id did not resolve to a school row.
 *  - `created: true`   → no per-school config existed and one was cloned.
 *  - `created: false` with `resolved: true` and no `error` → a config already
 *    existed for that (school, sync_type); nothing was minted.
 *  - `error`           → the seed threw for that school; the other schools and
 *                        the catalog row are unaffected.
 */
export const PlatformSyncConfigSeedResultSchema = z.object({
  schoolId: z.string().describe('The school_id as supplied in the request (public id or slug)'),
  resolvedSchoolId: z.string().nullable().describe("The resolved school's public ID, or null if unresolved"),
  resolved: z.boolean().describe('Whether the school_id resolved to a school'),
  alreadyExisted: z.boolean().describe('Whether a per-school config for this sync_type existed before the seed'),
  created: z.boolean().describe('Whether a per-school config was minted by this call'),
  error: z.string().nullable().describe('Why the seed failed for this school, or null')
})

export type PlatformSyncConfigSeedResult = z.infer<typeof PlatformSyncConfigSeedResultSchema>

/**
 * POST response. `data.config` is the row that was created; `data.seeded` is
 * present only when `school_ids` was supplied.
 */
export const CreatePlatformSyncConfigCatalogResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    config: PlatformSyncConfigCatalogRowSchema,
    seeded: z.array(PlatformSyncConfigSeedResultSchema).nullable().describe('Null when no school_ids were supplied')
  })
})

export type CreatePlatformSyncConfigCatalogResponse = z.infer<typeof CreatePlatformSyncConfigCatalogResponseSchema>

/**
 * GET request (query). Reads back exactly one catalog row.
 */
export const GetPlatformSyncConfigCatalogRequestSchema = z.object({
  sync_type: z.string().min(1).describe('Sync type to read the catalog row for')
})

export type GetPlatformSyncConfigCatalogRequest = z.infer<typeof GetPlatformSyncConfigCatalogRequestSchema>

/**
 * GET response. A missing row is a 404, not a 200 with a null body — the whole
 * point of the read is to answer "does the catalog row exist yet".
 */
export const GetPlatformSyncConfigCatalogResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    config: PlatformSyncConfigCatalogRowSchema
  })
})

export type GetPlatformSyncConfigCatalogResponse = z.infer<typeof GetPlatformSyncConfigCatalogResponseSchema>

/* ------------------------------------------------------------------------- *
 * PATCH + LIST (SCH-1965) — the catalog MANAGER lane
 *
 * PATCH /internal/platform-data-sync/platform-config — edit the catalog row for
 *                                                      one sync type, keyed by
 *                                                      `sync_type`
 * GET   /internal/platform-data-sync/platform-config — list the whole catalog
 *                                                      when no `sync_type` is
 *                                                      supplied
 *
 * WHY A SECOND WRITER. Retiring a sync type used to need the Clerk-gated
 * super-admin PATCH (`/system/v1/platform-sync-configs/:public_id`), which the
 * operator/agent lane cannot mint a session for. This is the same write under
 * the internal API-key posture, keyed by `sync_type` rather than the row's
 * public ID so the caller never has to look the ID up first.
 *
 * PATCH NEVER CREATES. A sync type with no catalog row is a 404; minting is
 * `POST /platform-config` and stays that way, so a typo cannot quietly seed a
 * dead fleet default.
 *
 * ARCHIVING IS NOT DISABLING. `is_archived` only stops the seeder cloning the
 * type into new schools and stops drift updates — per-school
 * `PlatformDataSyncConfig` rows keep running on their own `is_enabled`. That is
 * why archiving while enabled school rows exist is a 409 listing them, and why
 * `disable_schools: true` exists: it runs the same bulk disable
 * `POST /sync-config` performs FIRST, then archives, so one call retires a type
 * cleanly.
 *
 * A FOSSIL is a catalog row whose `key` is no longer in the backend sync-type
 * registry (`isValidSyncType`). It can be read, listed and archived, but never
 * UNarchived — there is no step left to run it, so resurrecting it would only
 * re-seed rows that can never sync.
 * ------------------------------------------------------------------------- */

/**
 * PATCH request body. Every mutating field is optional, but at least one must be
 * present — an empty patch is a 400 rather than a silent 200 that wrote nothing.
 *
 * `reason` is REQUIRED (non-empty) when `is_enabled: false` or
 * `is_archived: true`, matching the disable contract of
 * `SetSyncConfigEnabledRequestSchema`: every turn-it-off write leaves an audit
 * trail naming why.
 */
export const PatchPlatformSyncConfigCatalogRequestSchema = z
  .object({
    sync_type: z.string().min(1).describe('Sync type whose catalog row to patch (e.g. PHOTO_ALBUM_CATEGORIES)'),
    frequency_minutes: z.number().int().min(1).optional().describe('New default staleness interval in minutes'),
    priority: SyncPrioritySchema.optional().describe('New default queue priority'),
    window: SyncWindowSchema.optional().describe('New default run window'),
    sync_group: SyncGroupSchema.optional().describe('New reporting group'),
    description: z.string().min(1).optional().describe('New human description'),
    is_enabled: z.boolean().optional().describe('New fleet-default is_enabled (requires `reason` when false)'),
    is_archived: z
      .boolean()
      .optional()
      .describe('Archive/unarchive the catalog row (requires `reason` when true; see the archive guardrail)'),
    disable_schools: z
      .boolean()
      .optional()
      .describe(
        'ONLY valid with `is_archived: true` (a 400 otherwise): disable every enabled per-school config for this sync type FIRST, then archive. Without it, enabled school rows are a 409.'
      ),
    push_to_schools: z
      .boolean()
      .optional()
      .describe("Push the patched defaults onto each school's PlatformDataSyncConfig row"),
    school_ids: z
      .array(z.string().min(1))
      .min(1)
      .optional()
      .describe('Restrict `push_to_schools` to these schools (public IDs (CUID)); all schools when omitted'),
    reason: z
      .string()
      .max(2000)
      .optional()
      .describe('Audit reason; REQUIRED when `is_enabled` is false or `is_archived` is true'),
    changed_by: z
      .string()
      .max(200)
      .optional()
      .describe("Optional caller identity for the audit trail (defaults to 'internal-api')")
  })
  .refine(
    data =>
      data.frequency_minutes !== undefined ||
      data.priority !== undefined ||
      data.window !== undefined ||
      data.sync_group !== undefined ||
      data.description !== undefined ||
      data.is_enabled !== undefined ||
      data.is_archived !== undefined ||
      data.push_to_schools === true,
    { message: 'at least one field to change is required' }
  )
  // `disable_schools` is the pre-pass of an ARCHIVE, never a write of its own.
  // On its own it would be accepted, disable nothing, and return a 200 that reads
  // as "the schools were turned off" — so it is refused at the schema.
  .refine(data => data.disable_schools !== true || data.is_archived === true, {
    message: 'disable_schools is only valid together with is_archived: true',
    path: ['disable_schools']
  })
  .refine(data => data.is_enabled !== false || (data.reason !== undefined && data.reason.trim().length > 0), {
    message: 'reason is required when disabling a sync configuration',
    path: ['reason']
  })
  .refine(data => data.is_archived !== true || (data.reason !== undefined && data.reason.trim().length > 0), {
    message: 'reason is required when archiving a sync configuration',
    path: ['reason']
  })

export type PatchPlatformSyncConfigCatalogRequest = z.infer<typeof PatchPlatformSyncConfigCatalogRequestSchema>

/**
 * A catalog row as the MANAGER lane carries it: the create/read row plus the two
 * facts only this lane surfaces.
 *
 *  - `isArchived` is read from the row COLUMN `is_archived`, which is what every
 *    reader in the sync pipeline filters on. (It used to be written into the
 *    JSON `value` instead, so archiving hid nothing — fixed in SCH-1965.)
 *  - `fossil` is true when the row's `key` is no longer a registered sync type.
 *    The single-row GET and the create response deliberately omit both; this
 *    shape is what the list and the PATCH return.
 */
export const PlatformSyncConfigCatalogManagedRowSchema = PlatformSyncConfigCatalogRowSchema.extend({
  isArchived: z.boolean().describe('Whether the catalog row is archived (the `is_archived` COLUMN)'),
  fossil: z.boolean().describe('True when the row key is no longer a registered sync type')
})

export type PlatformSyncConfigCatalogManagedRow = z.infer<typeof PlatformSyncConfigCatalogManagedRowSchema>

/**
 * Per-school outcome of a `push_to_schools` pass, as
 * `massUpdateSchoolSyncConfigForType` reports it. Unchanged semantics: a school
 * whose update fails is a per-school `success: false`, never a 5xx.
 */
export const PlatformSyncConfigPushResultSchema = z.object({
  schoolId: z.string().describe("The school's public ID"),
  schoolName: z.string(),
  success: z.boolean(),
  error: z.string().optional().describe('Why the push failed for this school'),
  configsUpdated: z.number().int().describe('How many PlatformDataSyncConfig rows the push wrote')
})

export type PlatformSyncConfigPushResult = z.infer<typeof PlatformSyncConfigPushResultSchema>

export const PlatformSyncConfigPushResultsSchema = z.object({
  totalSchools: z.number().int(),
  successfulUpdates: z.number().int(),
  failedUpdates: z.number().int(),
  results: z.array(PlatformSyncConfigPushResultSchema)
})

export type PlatformSyncConfigPushResults = z.infer<typeof PlatformSyncConfigPushResultsSchema>

/**
 * Result of the `disable_schools: true` pre-pass. Same shape as the
 * `POST /sync-config` bulk toggle's `data` block, because it is the same
 * primitive (`setSyncConfigEnabled`) over the schools the guardrail found —
 * per-school best-effort, never a 4xx/5xx for one school.
 */
export const PlatformSyncConfigBulkDisableResultSchema = z.object({
  totalRequested: z.number().int().describe('How many enabled school configs the guardrail found'),
  found: z.number().int().describe('How many of those still had a non-archived config when written'),
  changed: z.number().int().describe('How many actually flipped from enabled to disabled'),
  results: z.array(SyncConfigToggleResultSchema)
})

export type PlatformSyncConfigBulkDisableResult = z.infer<typeof PlatformSyncConfigBulkDisableResultSchema>

/**
 * PATCH response. `disabledSchools` is present only when `disable_schools: true`
 * actually ran a disable pass; `push` only when `push_to_schools: true`.
 */
export const PatchPlatformSyncConfigCatalogResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    config: PlatformSyncConfigCatalogManagedRowSchema,
    disabledSchools: PlatformSyncConfigBulkDisableResultSchema.nullable().describe(
      'Null unless `disable_schools: true` ran the pre-archive bulk disable'
    ),
    push: PlatformSyncConfigPushResultsSchema.nullable().describe('Null unless `push_to_schools: true` was requested')
  })
})

export type PatchPlatformSyncConfigCatalogResponse = z.infer<typeof PatchPlatformSyncConfigCatalogResponseSchema>

/**
 * The 409 body's `error.details` when archiving is refused. The slugs are the
 * actionable part: they are exactly what `POST /sync-config` (or
 * `disable_schools: true`) needs to act on.
 */
export const PlatformSyncConfigArchiveConflictDetailsSchema = z.object({
  enabledSchools: z.object({
    count: z.number().int().describe('How many schools still have this sync type enabled'),
    slugs: z.array(z.string()).describe('Their slugs, sorted')
  })
})

export type PlatformSyncConfigArchiveConflictDetails = z.infer<typeof PlatformSyncConfigArchiveConflictDetailsSchema>

/**
 * LIST request (query). This is the SAME route as the single-row GET: supplying
 * `sync_type` keeps the original single-row behaviour (404 on a missing row),
 * omitting it lists the catalog.
 *
 * Archived rows are excluded by default so the list answers "what is live";
 * `include_archived=true` adds them, which is how a retirement is verified.
 * Fossils are always listed (flagged, not dropped) — the previous behaviour
 * discarded them with a log warning, so nothing surfaced them to an operator.
 */
export const ListPlatformSyncConfigCatalogRequestSchema = z.object({
  include_archived: z.boolean().default(false).describe('Include archived catalog rows (default false)')
})

export type ListPlatformSyncConfigCatalogRequest = z.infer<typeof ListPlatformSyncConfigCatalogRequestSchema>

/**
 * LIST response. Rows are ordered by `syncType`.
 */
export const ListPlatformSyncConfigCatalogResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    total: z.number().int().describe('How many rows are in `configs`'),
    includeArchived: z.boolean().describe('Whether archived rows were included'),
    archived: z.number().int().describe('How many of the returned rows are archived'),
    fossils: z.number().int().describe('How many of the returned rows are fossils'),
    configs: z.array(PlatformSyncConfigCatalogManagedRowSchema)
  })
})

export type ListPlatformSyncConfigCatalogResponse = z.infer<typeof ListPlatformSyncConfigCatalogResponseSchema>
